import { Fragment } from "react";
import { ARROW_ICON, STEPS } from "./howItWorksContent";
import StepIcon from "./StepIcon";

/**
 * Small chevron rendered between steps (mobile) or between cards (desktop).
 *
 * `self-center` keeps it vertically centred against the step columns, which are
 * top-aligned so their icons line up when a title wraps to two lines.
 */
function StepArrow() {
  return <img src={ARROW_ICON} alt="" aria-hidden="true" className="self-center shrink-0 w-3 h-3" />;
}

/**
 * The explainer that overlaps the bottom of the hero.
 *
 * The two breakpoints are drawn as different things rather than one reflowed
 * layout: mobile is a single card of three side-by-side step titles, desktop is
 * three separate cards carrying the descriptions too.
 *
 * "Find out more" opens HowItWorksModal, which the page mounts at its root so
 * the sheet escapes the hero's `relative z-10` stacking context.
 */
export default function HowItWorksSteps({ onOpenDetails }: { onOpenDetails: () => void }) {
  return (
    <>
      <div className="lg:hidden flex flex-col items-center w-full p-4 gap-2 bg-white rounded-lg">
        <div className="w-full text-sm font-bold leading-5 text-black text-left">HOW IT WORKS</div>

        <div className="flex items-start w-full gap-1">
          {STEPS.map(({ title, icon }, index) => (
            <Fragment key={title}>
              {index > 0 && <StepArrow />}

              <div className="flex flex-col items-center flex-1 min-w-0 gap-1">
                <StepIcon src={icon} step={index + 1} />
                <div className="text-sm font-bold leading-5 text-dark-1 text-center">{title}</div>
              </div>
            </Fragment>
          ))}
        </div>

        <button
          type="button"
          onClick={onOpenDetails}
          className="text-xs font-medium leading-4 text-blue underline underline-offset-2 hover:text-blue-1 transition-colors">
          Find out more
        </button>
      </div>

      <div className="hidden lg:grid lg:grid-cols-3 gap-2">
        {STEPS.map(({ title, description, icon }, index) => (
          <div key={title} className="flex items-center w-full p-4 gap-4 bg-white rounded-lg backdrop-blur-[6px]">
            <StepIcon src={icon} step={index + 1} />

            <div className="flex flex-col flex-1 min-w-0 gap-1">
              <div className="text-base font-bold leading-[22px] text-dark-1">{title}</div>
              <p className="text-sm font-medium leading-5 tracking-[0] text-gray-1">{description}</p>
            </div>

            {index < STEPS.length - 1 && <StepArrow />}
          </div>
        ))}
      </div>
    </>
  );
}
